import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

export default function FlightPathEditor() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/content/visited_locations").then((r) => setData(r.data?.data || null));
    }, []);

    if (!data) return <div className="text-[var(--sb-text-dim)]">Loading…</div>;

    const setField = (k, v) => setData({ ...data, [k]: v });
    const setPin = (i, patch) => {
        const pins = [...(data.pins || [])];
        pins[i] = { ...pins[i], ...patch };
        setData({ ...data, pins });
    };
    const movePin = (i, dir) => {
        const pins = [...(data.pins || [])];
        const j = i + dir;
        if (j < 0 || j >= pins.length) return;
        [pins[i], pins[j]] = [pins[j], pins[i]];
        setData({ ...data, pins });
    };
    const removePin = (i) => {
        if (!window.confirm("Remove this pin?")) return;
        const pins = (data.pins || []).filter((_, idx) => idx !== i);
        setData({ ...data, pins });
    };
    const addPin = () => {
        const id = `pin-${Date.now().toString(36)}`;
        setData({
            ...data,
            pins: [...(data.pins || []), { id, name: "New stop", note: "", lat: 56.5, lng: -4.0, label_anchor: "left" }],
        });
    };
    const save = async () => {
        setBusy(true);
        try {
            await api.put("/content/visited_locations", { data });
            clearContentCache();
            toast.success("Flight path saved.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };
    const reset = async () => {
        if (!window.confirm("Reset the flight path to its original 7 pins?")) return;
        setBusy(true);
        try {
            const { data: d } = await api.post("/content/visited_locations/reset");
            setData(d.data);
            clearContentCache();
            toast.success("Reset.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="flightpath-editor">
            <div className="card p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="font-serif text-2xl tracking-tight">Section copy</h2>
                    <div className="flex gap-2">
                        <button onClick={reset} disabled={busy} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}} data-testid="flightpath-reset"><RotateCcw size={14}/> Reset</button>
                        <button onClick={save} disabled={busy} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="flightpath-save"><Save size={14}/> {busy ? "Saving…" : "Save"}</button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Overline (small label above title)"><input className="input-line" value={data.overline || ""} onChange={(e) => setField("overline", e.target.value)} /></Field>
                    <Field label="Title line 1"><input className="input-line" value={data.title_line_1 || ""} onChange={(e) => setField("title_line_1", e.target.value)} /></Field>
                    <Field label="Title line 2 (italic)"><input className="input-line" value={data.title_line_2 || ""} onChange={(e) => setField("title_line_2", e.target.value)} /></Field>
                    <Field label="Intro paragraph" full><textarea className="input-line" rows={3} value={data.intro || ""} onChange={(e) => setField("intro", e.target.value)} /></Field>
                </div>
            </div>

            <div className="card p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-2xl tracking-tight">Pins ({(data.pins || []).length})</h2>
                    <button onClick={addPin} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}} data-testid="flightpath-add-pin">
                        <Plus size={14}/> Add pin
                    </button>
                </div>
                <div className="space-y-4">
                    {(data.pins || []).map((pin, i) => (
                        <div key={pin.id || i} className="border border-[var(--sb-border)] p-5" data-testid={`flightpath-pin-${i}`}>
                            <div className="flex items-center justify-between mb-4 gap-3">
                                <div className="font-mono text-xs text-[var(--sb-text-muted)]">Stop {String(i + 1).padStart(2, "0")}</div>
                                <div className="flex gap-1">
                                    <button onClick={() => movePin(i, -1)} disabled={i === 0} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] px-2 py-1 text-xs disabled:opacity-30" title="Move up">↑</button>
                                    <button onClick={() => movePin(i, 1)} disabled={i === (data.pins.length - 1)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] px-2 py-1 text-xs disabled:opacity-30" title="Move down">↓</button>
                                    <button onClick={() => removePin(i)} className="text-[var(--sb-text-dim)] hover:text-red-400 px-2 py-1" data-testid={`flightpath-remove-${i}`}><Trash2 size={14}/></button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Location name"><input className="input-line" value={pin.name || ""} onChange={(e) => setPin(i, { name: e.target.value })} data-testid={`flightpath-name-${i}`} /></Field>
                                <Field label="Label position">
                                    <select className="input-line bg-[var(--sb-surface)]" value={pin.label_anchor || "left"} onChange={(e) => setPin(i, { label_anchor: e.target.value })}>
                                        <option value="left">Right of pin</option>
                                        <option value="right">Left of pin</option>
                                    </select>
                                </Field>
                                <Field label="Latitude"><input type="number" step="0.0001" className="input-line" value={pin.lat ?? ""} onChange={(e) => setPin(i, { lat: parseFloat(e.target.value) })} /></Field>
                                <Field label="Longitude"><input type="number" step="0.0001" className="input-line" value={pin.lng ?? ""} onChange={(e) => setPin(i, { lng: parseFloat(e.target.value) })} /></Field>
                                <Field label="Your note about this place" full><textarea className="input-line" rows={2} value={pin.note || ""} onChange={(e) => setPin(i, { note: e.target.value })} data-testid={`flightpath-note-${i}`} /></Field>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <button onClick={reset} disabled={busy} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><RotateCcw size={14}/> Reset all</button>
                <button onClick={save} disabled={busy} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}}><Save size={14}/> {busy ? "Saving…" : "Save all"}</button>
            </div>
        </div>
    );
}


